import { useTranslation } from "react-i18next";
import { updateSignOnTabSettings } from "services/company.service";
import { CompanyEntity } from "types/Company";
import { Field, FIELD_INPUT_CLASS } from "../catalog-form-parts";
import { saveErrorKey } from "../save-error";
import {
  ChangeLink,
  EditPanel,
  SettingRow,
  SettingsCard,
  Toggle,
  useInlineEdit,
  useSaveWithToast,
} from "../ui";

// The server's codes for a refused save, and the text each one gets.
const SIGNONTAB_ERROR_KEYS = {
  SIGNONTAB_DATA_REQUIRED: "settings_signing_signontab_data_required",
  SIGNONTAB_SECRET_REQUIRED: "settings_signing_signontab_secret_required",
  SIGNONTAB_SECRET_INVALID: "settings_signing_signontab_secret_too_short",
};

interface SignOnTabConnectTabProps {
  companyId: string;
  /** The secret arrives as its last 4 characters only. */
  signontab: CompanyEntity["signontab"];
  /** Loads the company again; a save is done when that has finished. */
  onRefresh: () => Promise<unknown>;
}

export default function SignOnTabConnectTab({
  companyId,
  signontab,
  onRefresh,
}: SignOnTabConnectTabProps) {
  const { t } = useTranslation();
  // The secret is never loaded into the form: an empty field keeps the stored one.
  const edit = useInlineEdit({
    domain: signontab?.domain ?? "",
    clientId: signontab?.clientId ?? "",
    secret: "",
  });
  const switchSave = useSaveWithToast();
  const connectionSave = useSaveWithToast();

  const hasConnection = Boolean(signontab?.clientSecret);
  // With nothing saved, switching on opens the editor (there is nothing to save
  // yet), and saving that first connection also switches SignOnTab on.
  const isOn = Boolean(signontab?.isActive) || (edit.editing && !hasConnection);

  const draftDomain = edit.draft.domain.trim();
  const draftClientId = edit.draft.clientId.trim();
  // The server keeps the stored secret only for the same address and client ID.
  const needsSecretAgain =
    hasConnection &&
    !edit.draft.secret.trim() &&
    (draftDomain !== (signontab?.domain ?? "").trim() ||
      draftClientId !== (signontab?.clientId ?? "").trim());

  const setField = (field: keyof typeof edit.draft, value: string) =>
    edit.setDraft({ ...edit.draft, [field]: value });

  const send = (changes: {
    domain: string;
    clientId: string;
    clientSecret?: string;
    isActive: boolean;
  }) => updateSignOnTabSettings(changes, companyId).then(onRefresh);
  const saveOptions = {
    errorMessage: (err: unknown) => t(saveErrorKey(err, SIGNONTAB_ERROR_KEYS)),
  };

  const changeSwitch = async (enabled: boolean) => {
    if (!hasConnection) {
      if (enabled) edit.start();
      else edit.cancel();
      return;
    }
    await switchSave.save(
      () =>
        send({
          domain: signontab?.domain ?? "",
          clientId: signontab?.clientId ?? "",
          isActive: enabled,
        }),
      saveOptions
    );
  };

  const saveConnection = async () => {
    const saved = await connectionSave.save(
      () =>
        send({
          domain: draftDomain,
          clientId: draftClientId,
          clientSecret: edit.draft.secret.trim(),
          isActive: isOn,
        }),
      saveOptions
    );
    if (saved) edit.cancel();
  };

  return (
    <SettingsCard
      title="SignOnTab"
      description={t("settings_signing_signontab_description")}
      actions={
        <Toggle
          label={t("settings_signing_signontab_switch")}
          checked={isOn}
          disabled={switchSave.saving || connectionSave.saving}
          onChange={changeSwitch}
        />
      }
    >
      <SettingRow
        label={t("settings_signing_signontab_login_label")}
        description={t("settings_signing_signontab_login_hint")}
        value={
          hasConnection ? (
            <>
              <p>
                {t("settings_signing_domain")}: {signontab?.domain}
              </p>
              <p>
                {t("settings_signing_client_id")}: {signontab?.clientId}
              </p>
              <p>
                {t("settings_signing_secret")}:{" "}
                {t("settings_secret_entered_last4", {
                  last4: signontab?.clientSecret,
                })}
              </p>
            </>
          ) : (
            t("settings_none")
          )
        }
        action={!edit.editing && <ChangeLink onClick={edit.start} />}
      >
        {edit.editing && (
          <EditPanel
            saving={connectionSave.saving}
            error={connectionSave.error}
            onSave={saveConnection}
            onCancel={edit.cancel}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                htmlFor="signontab-domain"
                label={t("settings_signing_domain")}
              >
                <input
                  id="signontab-domain"
                  className={FIELD_INPUT_CLASS}
                  value={edit.draft.domain}
                  onChange={(e) => setField("domain", e.target.value)}
                />
              </Field>
              <Field
                htmlFor="signontab-client-id"
                label={t("settings_signing_client_id")}
              >
                <input
                  id="signontab-client-id"
                  className={FIELD_INPUT_CLASS}
                  value={edit.draft.clientId}
                  onChange={(e) => setField("clientId", e.target.value)}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field
                  htmlFor="signontab-secret"
                  label={t("settings_signing_secret")}
                >
                  <input
                    id="signontab-secret"
                    type="password"
                    autoComplete="new-password"
                    className={`${FIELD_INPUT_CLASS} max-w-md`}
                    value={edit.draft.secret}
                    placeholder={
                      hasConnection
                        ? t("settings_secret_keep_placeholder")
                        : undefined
                    }
                    onChange={(e) => setField("secret", e.target.value)}
                  />
                </Field>
                {needsSecretAgain && (
                  <p className="mt-1 text-xs text-amber-700">
                    {t("settings_signing_signontab_secret_required")}
                  </p>
                )}
              </div>
            </div>
          </EditPanel>
        )}
      </SettingRow>
    </SettingsCard>
  );
}
